import React, { FC, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AdFormLayout } from '@/components/AdForm/components/AdFormLayout';
import { CategorySelectBox } from '@/components/AdForm/components/CategorySelect/components/CategorySelectBox';
import { CategorySelectDrawer } from '@/components/AdForm/components/CategorySelect/components/CategorySelectDrawer';
import { Category } from '@/api/types/categories.types';
import { CreateAdForm } from '@/components/AdForm';
import { AdFormFields, CreateAdError } from '@/components/AdForm/AdForm.types';
import { ContactType, NewAdDefaultValues } from '@/api/types/ads.types';
import { formatPhoneNumber } from '@/utils/helpers';
import { useAuthStore } from '@/store/useAuthStore';
import { BannedView } from '@/views/Ads/Banned.view';
import { NewAdContextProvider } from '@/contexts/NewAdContext';
import useScreenSize from '@/hooks/useScreenSize';
import { ErrorCodesEnum } from '@/api/types/common.types';
import { useAdSubmissionFlow } from '@/hooks/useAdSubmissionFlow';

export const NewAdView: FC<NewAdDefaultValues> = ({
  categories = [],
  defaultRegion,
  defaultCategory,
  defaultSubcategory,
}) => {
  const { t } = useTranslation();
  const { isLarge } = useScreenSize();
  const router = useRouter();
  const userInfo = useAuthStore(state => state.userInfo);
  const { isBanned, name, email, authenticationPhone } = userInfo ?? {};
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<Category | null>(defaultSubcategory || null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    defaultCategory || null
  );
  const [isFormView, setFormView] = useState(!!defaultSubcategory);
  const [isBannedView, setBannedView] = useState(isBanned);
  useAdSubmissionFlow((defaultCategory ?? defaultSubcategory)?.id || '');

  useEffect(() => {
    setBannedView(isBanned);
  }, [isBanned]);

  const defaultValues: AdFormFields = {
    categoryId: '',
    collection: {},
    boolean: {},
    range: {},
    contact: {
      name: name || '',
      email: email || '',
      phone: authenticationPhone ? formatPhoneNumber(authenticationPhone) : '',
      contactType: ContactType.CallsAndMessages,
    },
    title: '',
    body: '',
    price: '',
    regionId: defaultRegion?.id || '',
    photoIds: [],
  };

  const methods = useForm<AdFormFields>({
    defaultValues,
    resetOptions: { keepDefaultValues: true },
  });

  const onSelectCategory = (category: Category | null) => {
    setSelectedCategory(category);
  };

  const onSelectSubcategory = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory);
    setFormView(true);
  };

  function onClose() {
    router.back();
  }

  function resetForm() {
    methods.reset(defaultValues);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
  }

  function onSubmitError(errors: CreateAdError[]) {
    const hasUserBannedError = errors.find(
      error => error.extensions?.code === ErrorCodesEnum.UserBannedError
    );

    if (hasUserBannedError) {
      setBannedView(true);
    }
  }

  const renderCategorySelect = () =>
    isLarge ? (
      <AdFormLayout onClose={onClose}>
        <CategorySelectBox onSelectSubcategory={onSelectSubcategory} />
      </AdFormLayout>
    ) : (
      <CategorySelectDrawer
        isOpen
        onClose={onClose}
        onSelectSubcategory={onSelectSubcategory}
      />
    );

  if (isBannedView) {
    return <BannedView description={t('pages.new_ad.banned_message')} />;
  }

  return (
    <NewAdContextProvider
      categories={categories}
      selectedCategory={selectedCategory}
      defaultSubcategory={defaultSubcategory}
      selectedSubcategory={selectedSubcategory}
      setSelectedCategory={onSelectCategory}
      setSelectedSubcategory={onSelectSubcategory}
    >
      <FormProvider {...methods}>
        {isFormView ? (
          <AdFormLayout onClose={onClose} onReset={resetForm} appearance="form">
            <CreateAdForm onSubmitError={onSubmitError} />
          </AdFormLayout>
        ) : (
          renderCategorySelect()
        )}
      </FormProvider>
    </NewAdContextProvider>
  );
};
